import { CountryCode } from "@silversea-ssc/dotcom-read-model-types/Common";
import {
  FacetSource,
  PublicFacetSource
} from "components/FindYourCruise/Components/Autocomplete/buildSources";
import { algoliaPromoValueToAlgoliaCode } from "components/FindYourCruise/Components/Filters/promo-filters";
import { BrowserRouteState } from "components/GlobalSearch/routing";
import type { IndexUiState, UiState } from "instantsearch.js";
import { isPresent } from "src/utils/checks";
import { Language } from "src/utils/localization/languages/types";

export type PromoAlgoliaCodeMapping = Record<string, number>;

export const uiStateToSerializableParams = (
  uiState: UiState,
  indexName: string,
  promoAlgoliaCodeMapping: PromoAlgoliaCodeMapping
): BrowserRouteState =>
  searchStateToSerializableParams(uiState[indexName], promoAlgoliaCodeMapping);

export const searchStateToSerializableParams = (
  searchState: UiState[string],
  promoAlgoliaCodeMapping: PromoAlgoliaCodeMapping
): BrowserRouteState => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { configure, ...filteredSearchState } = searchState;
  return {
    ...filteredSearchState,
    refinementList: toPublicFacet(
      filteredSearchState?.refinementList,
      promoAlgoliaCodeMapping
    )
  };
};

const localizedFacets = [
  "destinationNames",
  "countryNames"
] as LocalizedFacet[];
type LocalizedFacet = "destinationNames" | "countryNames";

export const toLocalizedFacets = (
  language: Language,
  countryCode: CountryCode | undefined,
  refinementList: IndexUiState["refinementList"],
  promoAlgoliaCodeMapping: PromoAlgoliaCodeMapping
): IndexUiState["refinementList"] => {
  console.debug("localizeFacet", refinementList);
  if (!refinementList) return refinementList;

  return Object.keys(refinementList).reduce(
    (acc, facetName) => {
      if (isLocalizableFacetSource(facetName)) {
        acc[localizedFacetSource(language, facetName)] =
          refinementList[facetName];
        delete acc[unlocalizedFacetSource(facetName)];
      }

      if (facetName === "promoCodes" && countryCode) {
        const values = refinementList[facetName]
          .map((code) =>
            algoliaPromoConfigurationCodeToAlgoliaCode(
              promoAlgoliaCodeMapping,
              code
            )
          )
          .filter(isPresent);

        acc[`countries.${countryCode}.pc`] = values;
        delete acc[facetName];
      }

      return acc;
    },
    { ...refinementList }
  );
};

const toPublicFacet = (
  refinementList: IndexUiState["refinementList"],
  promoAlgoliaCodeMapping: PromoAlgoliaCodeMapping
) => {
  if (!refinementList) return refinementList;

  return Object.keys(refinementList).reduce(
    (acc, facetName) => {
      if (isLocalizedFacetSource(facetName)) {
        acc[unlocalizedFacetSource(facetName)] = refinementList[facetName];
        delete acc[facetName];
      }

      if (isPromoFacet(facetName)) {
        const currentValue = refinementList[facetName] || [];

        const allNewValues = currentValue
          .map((value) =>
            algoliaCodeToPromoConfigurationCode(promoAlgoliaCodeMapping, value)
          )
          .filter(isPresent);

        acc["promoCodes"] = allNewValues;
        delete acc[facetName];
      }

      return acc;
    },
    { ...refinementList }
  );
};

const algoliaCodeToPromoConfigurationCode = (
  promoAlgoliaCodeMapping: PromoAlgoliaCodeMapping,
  algoliaCode: string
) => {
  const number = algoliaPromoValueToAlgoliaCode(algoliaCode);
  const promoCode = Object.entries(promoAlgoliaCodeMapping).find(
    ([, value]) => value === number
  );
  return promoCode ? promoCode[0] : undefined;
};

const algoliaPromoConfigurationCodeToAlgoliaCode = (
  promoAlgoliaCodeMapping: PromoAlgoliaCodeMapping,
  promoCode: string
): string | undefined => {
  const algoliaCode = Object.entries(promoAlgoliaCodeMapping).find(
    ([key]) => key === promoCode
  );
  return algoliaCode ? algoliaCode[1].toString() : undefined;
};

const isPromoFacet = (facetName: string) =>
  facetName.match(/^countries.{2,}.pc/g) !== null;

export const unlocalizedFacetSource = <T extends string, C extends string>(
  facet: T
): C => facet.split(".", 2)[0] as C;

export const localizedFacetSource = <
  T extends PublicFacetSource,
  C extends FacetSource
>(
  language: Language,
  facet: T
): C => {
  if (!isLocalizedFacetSource(facet)) return facet as unknown as C;

  const sanityCode = language.sanityCode;
  return `${facet}.${sanityCode}` as C;
};

const isLocalizedFacetSource = (
  facetName: string
): facetName is FacetSource => {
  return localizedFacets.includes(unlocalizedFacetSource(facetName));
};

const isLocalizableFacetSource = (
  facetName: string
): facetName is LocalizedFacet => {
  return localizedFacets.includes(unlocalizedFacetSource(facetName));
};
